import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { observer } from "mobx-react-lite";

import { translationKeys } from "../../models/app-translation-keys";
import { applyWhitelabelSettings } from "../../models/functions/apply-whitelabel-settings.function";
import { loadTranslationLanguages } from "../../services/languages";
import { useCurrentUserService, useSettingService } from "../../services/ServiceHooks";
import { useWhitelabelService } from "../../services/ServiceHooks/whitelabelHook";
import { useStore } from "../../stores/StoreContext";
import { MainComponent } from "../Layout";
import { LoadingPage } from "../Pages/FullScreenInformation";

const RawApp = () => {
  const { t } = useTranslation();
  const { userStore, navigationStore } = useStore();
  const storeContext = useStore();
  const location = useLocation();
  const currentUserService = useCurrentUserService();
  const settingSerivce = useSettingService();
  const whitelabelService = useWhitelabelService();

  const [isInitializing, setIsInitializing] = useState<boolean>(true);

  useEffect(() => {
    // Set the title of the page based on the current language
    // Use a timeout to ensure the title is set after the language has been changed
    const timeoutId = setTimeout(() => (document.title = t(translationKeys.app.title)), 100);

    return () => clearTimeout(timeoutId);
  }, [userStore.currentLanguage]);

  useEffect(() => {
    navigationStore.setActiveMenuItemBasedOnPath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const initializeGlobalSettings = async () => {
      try {
        applyWhitelabelSettings(await whitelabelService.getGeneralSettings(), storeContext);
        await currentUserService.ensureHasBudget();
        await currentUserService.ensureUserPermissions();
        await settingSerivce.ensureShouldPersistAnyUserActions();
      } catch (error) {
        console.error("Error initializing global settings:", error);
      } finally {
        setIsInitializing(false);
      }
    };

    if (isInitializing && userStore.userAccessToken) {
      initializeGlobalSettings();
    }
  }, [userStore.userAccessToken]);

  useEffect(() => {
    (async () => {
      await loadTranslationLanguages();
    })();
  }, []);

  if (isInitializing) {
    return <LoadingPage loadingInformation={t(translationKeys.global.loadingPage)} />;
  }

  return <MainComponent />;
};

export const App = observer(RawApp);
